/**
 * A fixed destination: somewhere that does not move and does not involve a second person.
 *
 * One shape backs all three uses, because they are the same act with a different fate:
 *
 *   pin        saved on this device, in `pins.ts`
 *   link       carried in the URL *fragment*, which browsers do not send
 *   rally      handed to a room, and the only one the server ever sees
 *
 * The first two never reach the server at all. That is not an implementation detail; it is the
 * privacy rule (`ProductOverview.md` §4.10).
 */

import { MAX_LABEL_LENGTH, SESSION, normalizeLabel, type LatLng } from '@beeline/shared';

// Labels are normalised in `@beeline/shared` because a rally point's label crosses the wire and
// has to be capped identically on both sides. A pin's label is the same idea, so it is the same
// function rather than a second one that can drift.
export { MAX_LABEL_LENGTH, normalizeLabel };

export interface Destination extends LatLng {
  label: string;
  /** Epoch milliseconds, for "pinned 40 minutes ago". Absent for link destinations. */
  pinnedAt?: number;
  /** The accuracy of the fix it was made from, so the UI can be honest about it. */
  accuracy?: number;
  /** Metres above the ellipsoid, where the pin was made somewhere the vertical matters. */
  altitude?: number | null;
  /**
   * A photo of what to look for, as a self-contained data URL.
   *
   * Present on pins and on rally points, absent on arrival links — a photo does not fit in a URL
   * fragment, and the fragment is the only place an arrival link is allowed to keep anything.
   */
  photo?: string | null;
}

/** Six decimal places is about 10 cm — far beyond GPS, and past it the URL is just noise. */
const round = (value: number): number => Math.round(value * 1e6) / 1e6;

const isLat = (value: number): boolean => Number.isFinite(value) && value >= -90 && value <= 90;
const isLng = (value: number): boolean => Number.isFinite(value) && value >= -180 && value <= 180;

/* -------------------------------------------------------------------------- */
/* Guided arrival — the URL fragment                                          */
/* -------------------------------------------------------------------------- */

/**
 * Build a guided-arrival link.
 *
 * The destination goes after the `#`, so it is never sent in the request. Even our own server
 * cannot see where the link points, which is exactly the intent.
 */
export function buildArrivalLink(destination: Destination, origin: string): string {
  const label = normalizeLabel(destination.label, 'Destination');
  const fragment = `dst=${round(destination.lat)},${round(destination.lng)},${encodeURIComponent(label)}`;
  return `${origin}/go#${fragment}`;
}

/** Read a destination back out of a fragment. Returns null for anything malformed. */
export function parseArrivalFragment(fragment: string): Destination | null {
  const params = new URLSearchParams(fragment.startsWith('#') ? fragment.slice(1) : fragment);
  const raw = params.get('dst');
  if (!raw) return null;

  // `label` may itself contain commas, so only split off the first two fields.
  const firstComma = raw.indexOf(',');
  const secondComma = raw.indexOf(',', firstComma + 1);
  if (firstComma < 0) return null;

  const lat = Number.parseFloat(raw.slice(0, firstComma));
  const lng = Number.parseFloat(
    secondComma < 0 ? raw.slice(firstComma + 1) : raw.slice(firstComma + 1, secondComma),
  );
  if (!isLat(lat) || !isLng(lng)) return null;

  let label = 'Destination';
  if (secondComma >= 0) {
    try {
      label = normalizeLabel(decodeURIComponent(raw.slice(secondComma + 1)), 'Destination');
    } catch {
      // A malformed escape sequence is not worth losing the coordinates over.
      label = normalizeLabel(raw.slice(secondComma + 1), 'Destination');
    }
  }

  return { lat, lng, label };
}

/* -------------------------------------------------------------------------- */

/** "pinned 40 minutes ago" — vague on purpose, because the minute never matters. */
export function describeAge(pinnedAt: number | undefined, now: number = Date.now()): string {
  if (!pinnedAt || !Number.isFinite(pinnedAt)) return 'pinned';

  const minutes = Math.round((now - pinnedAt) / 60_000);
  if (minutes < 1) return 'pinned just now';
  if (minutes === 1) return 'pinned a minute ago';
  if (minutes < 60) return `pinned ${minutes} minutes ago`;

  const hours = Math.round(minutes / 60);
  if (hours === 1) return 'pinned an hour ago';
  if (hours < 24) return `pinned ${hours} hours ago`;

  const days = Math.round(hours / 24);
  return days === 1 ? 'pinned yesterday' : `pinned ${days} days ago`;
}

/** Names offered for a pin. The name is the whole point — "my car" beats "spot 1". */
export const PIN_SUGGESTIONS = ['The car', 'The tent', 'The umbrella', 'Here'] as const;

/** ...and for a rally point, which is a place a group agreed on rather than one you left. */
export const RALLY_SUGGESTIONS = ['The main gate', 'The bar', 'The stage', 'The car park'] as const;

export const MAX_NAME_LENGTH = SESSION.maxNameLength;
